import {createContext, useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {
    getLoginOrigin,
    getStoredSession,
    removeLoginOrigin,
    removeStoredSession,
    setStoredSession
} from '../../LocalStorageService';
import {fetchAuthUser} from '../../utils/apiCalls';
import {clearStoredJobSelection} from '../JobContextProvider/JobContextProvider';

/**
 * AuthProvider — Single source of truth for authenticated user state.
 *
 * Responsibilities:
 * - Initializes from localStorage immediately (no loading state for logged-in users)
 * - Validates session with GET /auth/user/ once on mount
 * - Re-validates every 15 minutes (feature freshness)
 * - Updates context + localStorage when server data differs from cache
 * - Detects dead sessions (interceptor cleared localStorage) → redirects to /Landing
 * - Exposes login(data) for login flow and logout() for sign-out
 *
 * Response model (GET /api/v1/auth/user/):
 * @typedef {Object} AuthUser
 * @property {string} username
 * @property {string} email
 * @property {string} role - "admin" | "owner" | "creator"
 * @property {string[]} features - e.g. ["admin_dashboard","user","order","restore","track","destruction"]
 * @property {string} uuid - Admin: User.uuid | Customer: CustomerUser.uuid
 * @property {string} customer_uuid - "" for admin, Customer.uuid for customer users
 * @property {string} first_name
 * @property {string} last_name
 * @property {string} phone_number
 * @property {string} company_name - "" for admin, Customer.company_name for customer users
 * @property {string} arkbridge_customer_id - "" for admin, Customer.arkbridge_customer_id for customer users
 */

const AuthContext = createContext(null);

const REVALIDATION_INTERVAL_MS = 15 * 60 * 1000; // 15 minutes

/**
 * Asks the customer portal to end its own sessions as well, by flag on the address we return it to.
 *
 * It has to be an ask rather than something we do: the portal signs its customers in through Microsoft, and
 * MSAL caches that account under the portal's origin, not ours. We cannot reach it. Leave it and the portal's
 * sign-in button readmits the reader with no prompt at all, which is not a sign-out by any reading.
 *
 * The flag carries nothing and grants nothing — the portal acts on it by ending sessions held by that same
 * browser, so an unsolicited one can only sign its own visitor out.
 */
const PORTAL_SIGN_OUT_PARAM = 'signedOut';

const withSignOutRequest = (origin) => {
    try {
        const url = new URL(origin);
        url.searchParams.set(PORTAL_SIGN_OUT_PARAM, '1');
        return url.toString();
    } catch (error) {
        // Not reachable through EntraHandoff, which parses the origin before storing it. Returned unchanged
        // rather than dropped, so a malformed value still lands the reader somewhere.
        return origin;
    }
};

/*
 * Whether this document has started on its way to a sign-in page.
 *
 * A plain module variable and not state, deliberately. The thing being prevented here is a render, so the flag
 * that prevents it must not be one that causes one.
 *
 * Never cleared, and there is nothing to clear it for: the document is being replaced, and every reader is asking
 * about that one navigation.
 */
let signingOut = false;

/**
 * True once a sign-out, or a session found dead, has begun sending this document elsewhere.
 *
 * Read by the route guards in App.js. They decide what to render by reading localStorage as they render, the
 * store is emptied before the redirect is asked for, and the browser keeps painting until the destination
 * answers — so without this, anything that re-rendered in between put the Tape Ark sign-in form on screen.
 *
 * Not clearing the auth context stopped logout() causing that render itself. It could not stop any of the other
 * components from causing one, and one is enough.
 */
export const isSigningOut = () => signingOut;

/**
 * Sends a reader who no longer has a session back to wherever they sign in.
 *
 * For anyone who signed in here that is /Landing. For a customer who arrived from their own portal it is that
 * portal: /Landing is a Tape Ark username and password form, which they do not have and cannot get past, so
 * dropping them there ends the session at a dead end. EntraHandoff records the origin, and it records only what
 * the API named as the portal the sign-in came from — never a value out of the handoff address — so what is read
 * here has already been vetted. See sanitisePortalReturnUrl.
 *
 * The origin is deliberately *not* cleared here, and it used to be. One session is shared by every window of a
 * browser — one localStorage, one stored session — so when it ends, every one of those windows reaches this
 * function. Clearing the origin made it good for a single use: the first window to notice went to the portal and
 * the rest, finding nothing, went to /Landing. Two ordinary tabs and an expired session are enough to see it, and
 * the readers who saw it had just been signed out of a portal they were then not returned to.
 *
 * What that clearing was guarding against is a stale origin outliving the session it describes and sending some
 * later reader — quite possibly a Tape Ark one who signed in here — to a portal that is not theirs. That is now
 * handled where it belongs, at the start of the next session rather than the end of this one: login() clears it,
 * and EntraHandoff sets it immediately afterwards for the sign-ins that do have somewhere to return to.
 *
 * Used for sign-out and for a session found dead on revalidation, because both leave the reader in the same
 * position and both should end in the same place — but only the first asks the portal to sign out too. Someone
 * who pressed Sign Out means it; a session that merely expired should be allowed to resume without making the
 * reader authenticate to Microsoft again.
 *
 * Nothing must change React state on the way in here. See the note in logout().
 *
 * @param {Object} [options]
 * @param {boolean} [options.endPortalSession=false] - whether to ask the portal to end its sessions too.
 */
const redirectToSignIn = ({endPortalSession = false} = {}) => {
    // Set here rather than in logout(), because both callers leave the same emptied store behind a document that
    // carries on painting, and this is the line both of them reach.
    signingOut = true;

    const origin = getLoginOrigin();

    if (!origin) {
        window.location.href = '/Landing';
        return;
    }

    window.location.href = endPortalSession ? withSignOutRequest(origin) : origin;
};

export const AuthProvider = ({children}) => {
    // Initialize from localStorage immediately — no loading state
    const cached = getStoredSession();
    const [user, setUser] = useState(cached?.user ?? null);
    const intervalRef = useRef(null);

    const features = user?.features ?? [];
    const isAdmin = user?.role === 'admin';

    const isFeatureAvailable = useCallback(
        (feature) => features.includes(feature),
        [features]
    );

    /**
     * Validate session against server. Called on mount + every 15 min.
     * - If response differs from cache → update context + localStorage
     * - If session dead (interceptor cleared localStorage) → redirect
     * - If network error → silently keep cached state
     *
     * @param {boolean} [background=false] - Whether to keep the check out of the global loading wash.
     *   The revalidations on the timer are nobody's request and must be invisible: unmarked, each one
     *   washed the whole window out white for as long as it took, which reads as the page reloading
     *   itself. The call on mount is left visible — the reader is waiting on it to be let in.
     */
    const validate = useCallback((background = false) => {
        fetchAuthUser({background})
            .then(data => {
                if (!data) return;
                // Update context if server data differs
                setUser(prev => {
                    if (JSON.stringify(prev) === JSON.stringify(data)) return prev;
                    // Sync localStorage with fresh server data
                    const stored = getStoredSession();
                    if (stored) {
                        stored.user = data;
                        setStoredSession(stored);
                    }
                    return data;
                });
            })
            .catch(() => {
                // Only act if interceptor killed the session
                if (!getStoredSession()) {
                    // Not setUser(null), for the reason given in logout(): it would repaint this document as
                    // /Landing in the time the redirect takes to arrive.
                    stopInterval();
                    redirectToSignIn();
                }
                // Otherwise: network issue → silently keep cached state
            });
    }, []);

    const startInterval = useCallback(() => {
        stopInterval();
        // Wrapped rather than passed directly: setInterval hands its callback no arguments, and it is
        // the timer's calls, not the one on mount, that have to stay off the screen.
        intervalRef.current = setInterval(() => validate(true), REVALIDATION_INTERVAL_MS);
    }, [validate]);

    const stopInterval = useCallback(() => {
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    }, []);

    /**
     * Called by login pages after successful authentication.
     * Writes to localStorage + updates context + starts periodic validation.
     * @param {Object} data - Full login response: { access, refresh, user: AuthUser }
     */
    const login = useCallback((data) => {
        setStoredSession(data);
        // Whoever was here before does not get to scope this reader's dashboard. Cleared on the way in as
        // well as on the way out, because a session can end without logout() being called at all — an
        // expired refresh token is cleared by the axios interceptor and the reader is simply redirected.
        clearStoredJobSelection();
        /*
         * Same reasoning, for where this reader gets sent when they leave. A sign-out destination belongs to one
         * session, and this is the moment a session begins, so it is the moment the last one's destination stops
         * applying — whether or not that session ended tidily.
         *
         * This is the only place it is cleared for a sign-in that has nowhere of its own to return to, which is
         * every sign-in at /Landing. EntraHandoff calls this and then sets the origin, in that order, because a
         * portal sign-in does have somewhere and this line would otherwise take it straight back off again.
         */
        removeLoginOrigin();
        setUser(data.user);
        startInterval();
    }, [startInterval]);

    /**
     * Called by logout handlers.
     * Clears localStorage + stops interval + redirects.
     */
    const logout = useCallback(() => {
        stopInterval();
        removeStoredSession();
        // The job selection is in sessionStorage, which a logout does not touch: without this it outlives
        // the session and the next login at this tab opens on the last reader's selection. See
        // clearStoredJobSelection.
        clearStoredJobSelection();
        /*
         * The context is deliberately *not* cleared here, and this is the one thing in the function that has
         * to stay as it is.
         *
         * Setting the user to null is what asks React to render again, and every guarded route in App.js
         * decides what to show by calling getStoredSession() while it renders. The store has just been
         * emptied, so that render puts <Landing/> — the Tape Ark username and password form — on the screen.
         *
         * window.location only *starts* the navigation; the browser keeps painting this document until the
         * portal answers. So the last thing a customer saw on the way out of their own portal was somebody
         * else's sign-in page appearing for a fraction of a second, which reads as having been logged into
         * the wrong product.
         *
         * There is nothing to tidy: the document is being replaced, and the session it described is already
         * out of localStorage.
         */
        // Deliberate sign-out, so the portal is asked to end the Microsoft session as well. The dead-session
        // branch in validate() does not, since that reader has not asked to be signed out of anything.
        redirectToSignIn({endPortalSession: true});
    }, [stopInterval]);

    // On mount: validate once + start periodic interval (only if logged in)
    useEffect(() => {
        if (!cached) return; // Not logged in — nothing to validate
        validate();
        startInterval();
        return stopInterval;
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const value = useMemo(() => ({
        user,
        features,
        isAdmin,
        isFeatureAvailable,
        login,
        logout,
    }), [user, features, isAdmin, isFeatureAvailable, login, logout]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * Hook to access auth context from any component inside AuthProvider.
 * @returns {{ user: AuthUser|null, features: string[], isAdmin: boolean, isFeatureAvailable: (f: string) => boolean, login: (data: Object) => void, logout: () => void }}
 */
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) throw new Error('useAuth must be used within AuthProvider');
    return context;
};
